// ============================================================================
// RAID INLINE LIST
//
// What the badge expands into: the RAID items linked to one artefact, each
// closable in place. Deliberately terse — this sits inside a story row during
// a live workshop, so it shows the type, the title, who owns it and how to
// close it, and defers everything else to the modal.
// ============================================================================

import React, { useState } from 'react';
import { Pencil, Plus, User } from 'lucide-react';
import { RaidItem, RaidStatus } from '../../../types/raid';
import { RAID_TYPES, statusLabel, statusChip, ragChip } from './raidConfig';
import { RaidResolveInline } from './RaidResolveInline';

export interface RaidInlineListProps {
  items: RaidItem[];
  saving?: boolean;
  onResolve: (raidId: string, status: RaidStatus, resolution: string) => void;
  onEdit: (item: RaidItem) => void;
  onAdd?: () => void;
}

export function RaidInlineList({ items, saving, onResolve, onEdit, onAdd }: Readonly<RaidInlineListProps>) {
  const [resolving, setResolving] = useState<string | null>(null);

  return (
    <div className="mt-2 pt-2 border-t border-slate-200 flex flex-col gap-1.5">
      {items.map(item => {
        const cfg = RAID_TYPES[item.type];
        const Icon = cfg.icon;
        const isOpen = item.status === 'open';
        return (
          <div key={item.raidId} className="flex flex-col gap-1">
            <div className="flex items-start gap-2">
              <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold shrink-0 ${cfg.chip}`}>
                <Icon size={9} />{cfg.label}
              </span>

              <div className="flex-1 min-w-0">
                <p className={`text-xs leading-snug ${isOpen ? 'text-slate-700' : 'text-slate-400 line-through'}`}>
                  {item.title}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                  <span className={`px-1.5 py-0.5 rounded border text-[9px] font-semibold ${statusChip(item.status)}`}>
                    {statusLabel(item.type, item.status)}
                  </span>
                  {item.type === 'risk' && item.riskScore != null && (
                    <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold ${ragChip(item.rag)}`}>
                      {item.riskScore}
                    </span>
                  )}
                  {item.blockingSeverity === 'blocks' && isOpen && (
                    <span className="px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-red-600 text-[9px] font-semibold">
                      Blocks
                    </span>
                  )}
                  {item.owner && (
                    <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
                      <User size={9} />{item.owner}
                    </span>
                  )}
                  {item.dueDate && (
                    <span className="text-[10px] text-slate-400">due {item.dueDate}</span>
                  )}
                </div>
                {item.resolution && (
                  <p className="text-[10px] text-slate-400 italic mt-0.5">{item.resolution}</p>
                )}
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {isOpen && (
                  <button
                    type="button"
                    onClick={() => setResolving(prev => (prev === item.raidId ? null : item.raidId))}
                    className="text-[10px] font-semibold text-primary hover:text-primary-dark transition-colors"
                  >
                    Resolve
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onEdit(item)}
                  aria-label="Edit RAID item"
                  className="p-0.5 rounded hover:bg-primary/10 text-slate-400 hover:text-primary transition-colors"
                >
                  <Pencil size={11} />
                </button>
              </div>
            </div>

            {resolving === item.raidId && (
              <div className="pl-2 pb-1">
                <RaidResolveInline
                  compact
                  type={item.type}
                  currentStatus={item.status}
                  currentResolution={item.resolution}
                  saving={saving}
                  onResolve={(status, resolution) => {
                    onResolve(item.raidId, status, resolution);
                    setResolving(null);
                  }}
                  onCancel={() => setResolving(null)}
                />
              </div>
            )}
          </div>
        );
      })}

      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1 text-[11px] text-primary hover:text-primary-dark font-semibold transition-colors w-fit mt-0.5"
        >
          <Plus size={11} /> Raise another
        </button>
      )}
    </div>
  );
}
